import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, map, switchMap, tap } from 'rxjs';
import { Q8Breakpoints } from 'core';
import { Cart, UpdateCartProductResponse } from '../interfaces/cart.model';
import { CartDialog } from '../lib/components/cart-dialog/cart-dialog';

@Injectable({ providedIn: 'root' })
export class CartService {
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);
  private breakpointObserver = inject(BreakpointObserver);
  private dialogRef?: MatDialogRef<CartDialog>;

  // Carrello attivo caricato all'ingresso nella pagina prodotti;
  private readonly _cart = signal<Cart | null>(null);
  readonly cart = this._cart.asReadonly();

  // Il carrello attivo è l'ultimo carrello dell'utente non archiviato né in bozza;
  // se non esiste, il backend ne crea uno nuovo
  getActiveCart(): Observable<Cart> {
    return this.http.get<Cart>('api/carrelloapp').pipe(
      tap(cart => this._cart.set(cart))
    );
  }

  // Aggiunta dal catalogo: il backend sovrascrive la quantità della riga,
  // quindi se il prodotto è già nel carrello si somma la quantità richiesta
  // a quella esistente
  addProduct(idProdotto: string, quantita: number): Observable<UpdateCartProductResponse> {
    const existing = (this.cart()?.righe ?? [])
      .filter(row => !row.archiviata && row.idProdotto === idProdotto)
      .reduce((sum, row) => sum + row.quantita, 0);

    return this.updateProduct(idProdotto, existing + quantita);
  }

  // Imposta la quantità esatta della riga (0 = il backend la elimina); il
  // backend crea il carrello se non esiste e, senza prezzo, applica il
  // listino. Poi ricarica il carrello per aggiornare il signal
  updateProduct(idProdotto: string, quantita: number, prezzo?: number): Observable<UpdateCartProductResponse> {
    return this.http.post<UpdateCartProductResponse>('api/carrelloapp/righe', {
      idProdotto,
      quantita,
      ...(prezzo !== undefined ? { prezzo } : {}),
    }).pipe(
      switchMap(response =>
        this.getActiveCart().pipe(
          map(() => response)
        )
      )
    );
  }

  // Svuota il carrello attivo, poi ricarica il carrello per aggiornare il signal
  clearCart(): Observable<Cart> {
    return this.http.delete('api/carrelloapp').pipe(
      switchMap(() => this.getActiveCart())
    );
  }

  open() {
    if (this.dialogRef) {
      return;
    }

    const isMobile = this.breakpointObserver.isMatched(`(max-width: ${Q8Breakpoints.tablet - 1}px)`);

    this.dialogRef = this.dialog.open(CartDialog, {
      position: { top: '0', right: '0' },
      height: '100dvh',
      width: isMobile ? '100vw' : '400px',
      maxWidth: '100vw',
      panelClass: 'cart-panel',
      // Disabilita l'animazione di default (zoom centrale): lo slide da destra
      // è gestito via CSS sul pannello
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
    });
  }

  close() {
    this.dialogRef?.close();
  }
}
